import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    MdArrowBack, MdSensors, MdAdd, MdDelete,
    MdLocationOn, MdDeveloperBoard, MdExpandMore, MdExpandLess,
    MdEmail, MdPhone, MdPerson, MdEdit, MdSave, MdCancel,
    MdToggleOn, MdToggleOff, MdEditNote, MdTraffic, MdMemory, MdTune, MdGroup, MdPsychology
} from 'react-icons/md';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import AddSensorModal from '../../components/admin/AddSensorModal';
import AddCounterModal from '../../components/admin/AddCounterModal';
import AddSystemModal from '../../components/admin/AddSystemModal';
import RenameModal from '../../components/admin/RenameModal';
import SensorThresholdModal from '../../components/admin/SensorThresholdModal';
import AddStatusSignalModal from '../../components/admin/AddStatusSignalModal';
import AddPlcModal from '../../components/admin/AddPlcModal';
import CompanyUsersPanel from '../../components/admin/CompanyUsersPanel';
import { useLanguage } from '../../context/LanguageContext';

export default function CompanyDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useLanguage();
    const [company, setCompany] = useState(null);
    const [systems, setSystems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddSensor, setShowAddSensor] = useState(false);
    const [showAddCounter, setShowAddCounter] = useState(false);
    const [showAddSystem, setShowAddSystem] = useState(false);
    const [showAddStatusSignal, setShowAddStatusSignal] = useState(false);
    const [showAddPlc, setShowAddPlc] = useState(false);
    const [plcCards, setPlcCards] = useState([]);
    const [deletingId, setDeletingId] = useState(null);
    const [expandedSystems, setExpandedSystems] = useState({});
    const [renameConfig, setRenameConfig] = useState({ show: false, targetId: null, targetType: '', currentName: '', currentUnit: '' });
    const [thresholdSensor, setThresholdSensor] = useState(null);
    const [manualEnabled, setManualEnabled] = useState(false);
    const [togglingManual, setTogglingManual] = useState(false);
    const [showUsersPanel, setShowUsersPanel] = useState(false);

    // Profile edit state
    const [isEditing, setIsEditing] = useState(false);
    const [editForm, setEditForm] = useState({
        name: '', address: '', email: '', telNo: '', username: '', password: ''
    });

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [compRes, sysRes, countRes, plcRes] = await Promise.all([
                api.get(`/companies/${id}`),
                api.get(`/systems/company/${id}`),
                api.get(`/counters/company/${id}`),
                api.get(`/plc/company/${id}`)
            ]);
            const compData = compRes.data.data;
            setCompany(compData);
            setManualEnabled(compData?.manualDataConfig?.enabled ?? false);
            setEditForm({
                name: compData.name || '',
                address: compData.address || '',
                email: compData.email || '',
                telNo: compData.telNo || '',
                username: compData.username || '',
                password: ''
            });

            setPlcCards(plcRes.data.data || []);

            const sysData = sysRes.data.data || [];
            const countData = countRes.data.data || [];
            
            // Map counters to their systems
            const systemsWithCounters = sysData.map(s => ({
                ...s,
                counters: countData.filter(c => {
                    const sid = c.systemId?._id || c.systemId;
                    return sid && sid.toString() === s._id.toString();
                })
            }));

            setSystems(systemsWithCounters);
            const expanded = {};
            sysData.forEach(s => { expanded[s._id] = true; });
            setExpandedSystems(expanded);
        } catch {
            toast.error(t('companyDetail.dataLoadFailed'));
        } finally {
            setLoading(false);
        }
    }, [id, t]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleUpdateProfile = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            await api.put(`/companies/${id}`, editForm);
            toast.success(t('companyDetail.updated'));
            setIsEditing(false);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.message || t('companyDetail.updateFailed'));
        } finally {
            setLoading(false);
        }
    };

    const totalSensors = systems.reduce((sum, s) => sum + (s.sensors?.length || 0), 0);

    const handleDeleteSensor = async (sensorId, sensorName) => {
        if (!window.confirm(t('companyDetail.deleteSensorConfirm', { name: sensorName }))) return;
        try {
            setDeletingId(sensorId);
            await api.delete(`/sensors/${sensorId}`);
            toast.success(t('companyDetail.sensorDeleted'));
            fetchData();
        } catch {
            toast.error(t('companyDetail.sensorDeleteFailed'));
        } finally {
            setDeletingId(null);
        }
    };

    const handleDeleteCounter = async (counterId, counterName) => {
        if (!window.confirm(t('companyDetail.deleteCounterConfirm', { name: counterName }))) return;
        try {
            setDeletingId(counterId);
            await api.delete(`/counters/${counterId}`);
            toast.success(t('companyDetail.counterDeleted'));
            fetchData();
        } catch {
            toast.error(t('companyDetail.counterDeleteFailed'));
        } finally {
            setDeletingId(null);
        }
    };

    const handleDeleteSystem = async (systemId, systemName) => {
        if (!window.confirm(t('companyDetail.deleteSystemConfirm', { name: systemName }))) return;
        try {
            setDeletingId(systemId);
            await api.delete(`/systems/${systemId}`);
            toast.success(t('companyDetail.systemDeleted'));
            fetchData();
        } catch {
            toast.error(t('companyDetail.systemDeleteFailed'));
        } finally {
            setDeletingId(null);
        }
    };

    const openRenameModal = (targetType, targetId, currentName, currentUnit = '') => {
        setRenameConfig({ show: true, targetType, targetId, currentName, currentUnit });
    };

    const toggleSystem = (systemId) => {
        setExpandedSystems(prev => ({ ...prev, [systemId]: !prev[systemId] }));
    };

    const handleToggleManual = async () => {
        try {
            setTogglingManual(true);
            const newVal = !manualEnabled;
            await api.put(`/manual-data/${id}/config`, { enabled: newVal });
            setManualEnabled(newVal);
            toast.success(newVal ? t('companyDetail.manualDataEnabled') : t('companyDetail.manualDataDisabled'));
        } catch {
            toast.error(t('companyDetail.settingFailed'));
        } finally {
            setTogglingManual(false);
        }
    };

    if (loading && !company) return <div className="loading-page"><div className="spinner" style={{ width: 48, height: 48 }}></div></div>;
    if (!company) return <div className="loading-page"><p style={{ color: 'var(--text-secondary)' }}>{t('companyDetail.notFound')}</p></div>;

    return (
        <div className="page-wrapper">
            {/* 🏢 Company Hero Header */}
            <div className="page-header" style={{ marginBottom: 32, alignItems: 'flex-start' }}>
                <div className="page-header-left">
                    <button className="btn btn-secondary btn-icon" onClick={() => navigate(-1)} id="back-btn" style={{ marginRight: 16, marginTop: 4 }}>
                        <MdArrowBack />
                    </button>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
                            <h1 className="page-title" style={{ fontSize: 28, letterSpacing: '-0.02em', margin: 0 }}>{company.name}</h1>
                            <span className="badge badge-primary" style={{ fontSize: 10, padding: '4px 10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('companyDetail.active')}</span>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, color: 'var(--text-secondary)', fontSize: 13 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <MdLocationOn style={{ color: 'var(--primary)', opacity: 0.7 }} />
                                <span>{company.address}</span>
                            </div>
                            {company.email && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <MdEmail style={{ color: 'var(--primary)', opacity: 0.7 }} />
                                    <span>{company.email}</span>
                                </div>
                            )}
                            {company.telNo && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <MdPhone style={{ color: 'var(--primary)', opacity: 0.7 }} />
                                    <span>{company.telNo}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="action-toolbar" style={{ paddingTop: 8 }}>
                    <button
                        className="action-chip chip-hero"
                        onClick={() => navigate(`/admin/scada/${id}`)}
                        id="design-scada-btn"
                    >
                        <MdSensors />
                        <span>{t('companyDetail.scadaDesign')}</span>
                    </button>

                    <button className="action-chip accent-primary" onClick={() => setShowAddSystem(true)} id="add-system-btn">
                        <MdDeveloperBoard /> {t('companyDetail.addSystem')}
                    </button>
                    <button className="action-chip accent-primary" onClick={() => setShowAddSensor(true)} id="add-sensor-btn">
                        <MdAdd /> {t('companyDetail.addSensor')}
                    </button>
                    <button className="action-chip accent-warning" onClick={() => setShowAddCounter(true)} id="add-counter-btn">
                        <MdAdd /> {t('companyDetail.addCounter')}
                    </button>
                    <button className="action-chip accent-info" onClick={() => navigate(`/admin/companies/${id}/status-signals`)} id="manage-status-signals-btn">
                        <MdTraffic /> {t('companyDetail.manageStatusSignals')}
                    </button>
                    <button className="action-chip accent-info" onClick={() => setShowAddStatusSignal(true)} id="add-status-signal-btn">
                        <MdAdd /> {t('companyDetail.addSignal')}
                    </button>
                    <button className="action-chip accent-primary" onClick={() => setShowAddPlc(true)} id="add-plc-btn">
                        <MdMemory /> {t('plc.addPlcCard')}
                    </button>
                    <button className="action-chip accent-primary" onClick={() => navigate(`/admin/companies/${id}/ysa`)} id="go-ysa-btn">
                        <MdPsychology /> {t('companyDetail.ysa')}
                    </button>
                    <button
                        className="action-chip"
                        onClick={() => setShowUsersPanel(s => !s)}
                        id="toggle-users-panel-btn"
                    >
                        <MdGroup /> {t('companyUsers.title')}
                        {showUsersPanel ? <MdExpandLess /> : <MdExpandMore />}
                    </button>

                    {/* Manual Data Toggle + Navigate */}
                    <div className="action-toolbar-group">
                        <MdEditNote style={{ color: 'var(--primary)', fontSize: 18 }} />
                        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{t('companyDetail.manualData')}</span>
                        <button
                            onClick={handleToggleManual}
                            disabled={togglingManual}
                            id="toggle-manual-btn"
                            title={manualEnabled ? t('companyDetail.deactivate') : t('companyDetail.activate')}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 4,
                                padding: '4px 10px', borderRadius: 8, border: 'none', cursor: 'pointer',
                                fontWeight: 700, fontSize: 12, fontFamily: 'Inter, sans-serif',
                                background: manualEnabled ? 'rgba(34,197,94,0.12)' : 'var(--bg-body)',
                                color: manualEnabled ? 'var(--success)' : 'var(--text-muted)',
                                transition: 'all 0.2s'
                            }}
                        >
                            {manualEnabled ? <MdToggleOn size={18} /> : <MdToggleOff size={18} />}
                            {manualEnabled ? t('common.active') : t('common.passive')}
                        </button>
                        <button
                            className="btn btn-primary btn-sm"
                            onClick={() => navigate(`/admin/companies/${id}/manual-data`)}
                            id="go-manual-data-btn"
                            style={{ height: 32, padding: '0 12px', fontSize: 12 }}
                        >
                            {t('common.manage')}
                        </button>
                    </div>
                </div>
            </div>

            {/* 🎯 Centered Content Container */}
            <div style={{ maxWidth: 1200, margin: '0 auto', width: '100%' }}>
                {showUsersPanel && <CompanyUsersPanel companyId={id} />}
                <div className="detail-grid-2col" style={{ alignItems: 'start' }}>

                    {/* 👤 Profile Panel */}
                    <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
                        <div className="card" style={{ padding: 24 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                                <h3 style={{ margin: 0, fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <MdPerson style={{ color: 'var(--primary)' }} /> {t('companyDetail.companyInfo')}
                                </h3>
                                {!isEditing ? (
                                    <button onClick={() => setIsEditing(true)} className="btn btn-secondary btn-sm btn-icon" style={{ width: 32, height: 32 }}>
                                        <MdEdit />
                                    </button>
                                ) : (
                                    <div style={{ display: 'flex', gap: 4 }}>
                                        <button onClick={handleUpdateProfile} className="btn btn-primary btn-sm btn-icon" style={{ width: 32, height: 32 }}>
                                            <MdSave />
                                        </button>
                                        <button onClick={() => setIsEditing(false)} className="btn btn-secondary btn-sm btn-icon" style={{ width: 32, height: 32 }}>
                                            <MdCancel />
                                        </button>
                                    </div>
                                )}
                            </div>

                            {!isEditing ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                    <div style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: 12 }}>
                                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase' }}>{t('companyDetail.companyNameLabel')}</div>
                                        <div style={{ fontSize: 14, fontWeight: 600 }}>{company.name}</div>
                                    </div>
                                    <div style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: 12 }}>
                                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase' }}>{t('companyDetail.usernameLabel')}</div>
                                        <div style={{ fontSize: 14, fontWeight: 600 }}>{company.username || '—'}</div>
                                    </div>
                                    <div style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: 12 }}>
                                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase' }}>{t('companyDetail.address')}</div>
                                        <div style={{ fontSize: 14 }}>{company.address}</div>
                                    </div>
                                    <div style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: 12 }}>
                                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase' }}>{t('companyDetail.emailLabel')}</div>
                                        <div style={{ fontSize: 14 }}>{company.email || '—'}</div>
                                    </div>
                                    <div style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: 12 }}>
                                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase' }}>{t('companyDetail.phoneLabel')}</div>
                                        <div style={{ fontSize: 14 }}>{company.telNo || '—'}</div>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                    <div className="form-group">
                                        <label className="form-label" style={{ fontSize: 11 }}>{t('companyDetail.companyNameLabel')}</label>
                                        <input className="form-input form-input-sm" value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label" style={{ fontSize: 11 }}>{t('companyDetail.usernameLabel')}</label>
                                        <input className="form-input form-input-sm" value={editForm.username} onChange={e => setEditForm({ ...editForm, username: e.target.value })} autoComplete="username" />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label" style={{ fontSize: 11 }}>{t('companyDetail.address')}</label>
                                        <input className="form-input form-input-sm" value={editForm.address} onChange={e => setEditForm({ ...editForm, address: e.target.value })} />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label" style={{ fontSize: 11 }}>{t('companyDetail.emailLabel')}</label>
                                        <input className="form-input form-input-sm" type="email" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label" style={{ fontSize: 11 }}>{t('companyDetail.phoneLabel')}</label>
                                        <input className="form-input form-input-sm" value={editForm.telNo} onChange={e => setEditForm({ ...editForm, telNo: e.target.value })} />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label" style={{ fontSize: 11 }}>{t('companyDetail.loginPassword')}</label>
                                        <input className="form-input form-input-sm" type="password" placeholder="******" value={editForm.password} onChange={e => setEditForm({ ...editForm, password: e.target.value })} autoComplete="new-password" />
                                    </div>
                                </div>
                            )}

                            <div style={{ marginTop: 24, padding: 16, background: 'rgba(0,0,0,0.1)', borderRadius: 12 }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--primary)' }}>{systems.length}</div>
                                        <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase' }}>{t('common.system')}</div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--secondary)' }}>{totalSensors}</div>
                                        <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase' }}>{t('common.sensor')}</div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--warning)' }}>{systems.reduce((sum, s) => sum + (s.counters?.length || 0), 0)}</div>
                                        <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase' }}>{t('common.counter')}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* 🚀 Systems & Sensors Visualization */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                        {systems.length === 0 ? (
                            <div className="empty-state" style={{ padding: '60px 0' }}>
                                <div className="empty-state-icon" style={{ fontSize: 48 }}>⚡</div>
                                <div className="empty-state-title" style={{ fontSize: 18 }}>{t('companyDetail.noSystems')}</div>
                                <div className="empty-state-desc">{t('companyDetail.noSystemsDesc')}</div>
                                <button className="btn btn-primary" style={{ marginTop: 24 }} onClick={() => setShowAddSystem(true)}>
                                    <MdDeveloperBoard /> {t('companyDetail.createFirstSystem')}
                                </button>
                            </div>
                        ) : (
                            systems.map((system, si) => (
                                <motion.div key={system._id}
                                    initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: si * 0.05 }}
                                    style={{
                                        background: 'var(--bg-card)',
                                        border: '1px solid var(--border)',
                                        borderRadius: 16,
                                        overflow: 'hidden',
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.03)'
                                    }}
                                >
                                    {/* System Header */}
                                    <div style={{
                                        padding: '16px 24px',
                                        background: 'rgba(0,0,0,0.01)',
                                        borderBottom: '1px solid var(--border)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                            <div style={{ color: 'var(--success)', fontSize: 20 }}><MdDeveloperBoard /></div>
                                            <div>
                                                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{system.systemName}</div>
                                                <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500 }}>ID: {system._id.substring(0, 8)}...</div>
                                            </div>
                                            <div className="badge" style={{ background: 'var(--bg-body)', border: '1px solid var(--border)', fontSize: 10, color: 'var(--text-secondary)', padding: '2px 8px' }}>
                                                {t('companyDetail.deviceCount', { count: (system.sensors?.length || 0) + (system.counters?.length || 0) })}
                                            </div>
                                            {(() => {
                                                const systemPlc = plcCards.find(p => {
                                                    const plcSysId = p.systemId?._id || p.systemId;
                                                    return plcSysId && plcSysId.toString() === system._id.toString();
                                                });
                                                if (systemPlc) {
                                                    return (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, background: 'rgba(59,130,246,0.08)', color: 'var(--primary)', padding: '4px 10px', borderRadius: 8, fontWeight: 600 }}>
                                                            <MdMemory size={14} style={{ color: 'var(--primary)' }} />
                                                            <span>{systemPlc.plcName}</span>
                                                            <span style={{ opacity: 0.6, fontFamily: 'monospace', fontSize: 10 }}>({systemPlc.plcUUID.substring(0, 8)}...)</span>
                                                            <button 
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    navigate(`/admin/companies/${id}/plc/${systemPlc._id}`);
                                                                }} 
                                                                className="btn btn-link btn-xs"
                                                                style={{ padding: '0 4px', fontSize: 10, fontWeight: 700, textDecoration: 'underline', border: 'none', background: 'none', color: 'var(--primary)', cursor: 'pointer' }}
                                                            >
                                                                {t('plc.plcSettings')}
                                                            </button>
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            })()}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <button
                                                className="btn btn-primary btn-sm btn-icon"
                                                onClick={() => openRenameModal('system', system._id, system.systemName)}
                                                style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(79, 142, 247, 0.08)', border: 'none', color: 'var(--primary)' }}
                                                title={t('companyDetail.renameSystem')}
                                            >
                                                <MdEdit />
                                            </button>
                                            <button
                                                className="btn btn-danger btn-sm btn-icon"
                                                onClick={() => handleDeleteSystem(system._id, system.systemName)}
                                                disabled={deletingId === system._id}
                                                style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(239, 68, 68, 0.08)', border: 'none', color: 'var(--danger)' }}
                                            >
                                                {deletingId === system._id ? <span className="spinner spinner-sm" /> : <MdDelete />}
                                            </button>
                                            <button
                                                className="btn btn-secondary btn-sm btn-icon"
                                                onClick={() => toggleSystem(system._id)}
                                                style={{ width: 32, height: 32, borderRadius: 8 }}
                                            >
                                                {expandedSystems[system._id] ? <MdExpandLess /> : <MdExpandMore />}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Sensors List (Dense Table) */}
                                    <AnimatePresence>
                                        {expandedSystems[system._id] && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: 'auto', opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.2 }}
                                            >
                                                <div style={{ padding: '0 24px 16px' }}>
                                                    {!system.sensors?.length && !system.counters?.length ? (
                                                        <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                                                            {t('companyDetail.noDevices')}
                                                        </div>
                                                    ) : (
                                                        <div style={{ overflowX: 'auto' }}>
                                                            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}>
                                                                <thead>
                                                                    <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                                                                        <th style={{ padding: '12px 8px', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase' }}>{t('companyDetail.deviceName')}</th>
                                                                        <th style={{ padding: '12px 8px', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase' }}>{t('companyDetail.type')}</th>
                                                                        <th style={{ padding: '12px 8px', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase' }}>{t('common.unit')}</th>
                                                                        <th style={{ padding: '12px 8px', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase' }}>{t('companyDetail.deviceId')}</th>
                                                                        <th style={{ padding: '12px 8px', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', textAlign: 'right' }}>{t('common.actions')}</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {system.sensors?.map((sensor) => (
                                                                        <tr key={sensor._id} className="table-row-hover" style={{ borderBottom: '1px solid rgba(0,0,0,0.03)' }}>
                                                                            <td style={{ padding: '14px 8px' }}>
                                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                                                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--success)' }}></div>
                                                                                    <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{sensor.sensorName}</span>
                                                                                </div>
                                                                            </td>
                                                                            <td style={{ padding: '14px 8px' }}><span className="badge" style={{ fontSize: 9 }}>{t('common.sensor').toUpperCase()}</span></td>
                                                                            <td style={{ padding: '14px 8px' }}>
                                                                                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--primary)', background: 'rgba(79, 142, 247, 0.08)', padding: '2px 8px', borderRadius: 4 }}>
                                                                                    {sensor.unit}
                                                                                </span>
                                                                            </td>
                                                                            <td style={{ padding: '14px 8px' }}>
                                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'monospace' }}>
                                                                                    {sensor.sensorUUID}
                                                                                </div>
                                                                            </td>
                                                                            <td style={{ padding: '14px 8px', textAlign: 'right' }}>
                                                                                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                                                                                    <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/admin/sensors/${sensor.sensorUUID}`)} style={{ padding: '4px 12px', fontSize: 11, borderRadius: 6 }}>{t('companyDetail.viewData')}</button>
                                                                                    <button className="btn btn-primary btn-sm btn-icon" onClick={() => openRenameModal('sensor', sensor._id, sensor.sensorName, sensor.unit)} style={{ width: 28, height: 28, borderRadius: 6, background: 'rgba(79, 142, 247, 0.08)', border: 'none', color: 'var(--primary)' }} title={t('companyDetail.renameSensor')}><MdEdit /></button>
                                                                                    <button className="btn btn-secondary btn-sm btn-icon" onClick={() => setThresholdSensor(sensor)} style={{ width: 28, height: 28, borderRadius: 6, background: 'rgba(245,158,11,0.1)', border: 'none', color: 'var(--warning)' }} title={t('companyDetail.manageThreshold')}><MdTune /></button>
                                                                                    <button className="btn btn-danger btn-sm" onClick={() => handleDeleteSensor(sensor._id, sensor.sensorName)} disabled={deletingId === sensor._id} style={{ padding: '4px 12px', fontSize: 11, borderRadius: 6 }}>{t('common.delete')}</button>
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                    {system.counters?.map((counter) => (
                                                                        <tr key={counter._id} className="table-row-hover" style={{ borderBottom: '1px solid rgba(0,0,0,0.03)' }}>
                                                                            <td style={{ padding: '14px 8px' }}>
                                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                                                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--warning)' }}></div>
                                                                                    <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{counter.counterName}</span>
                                                                                </div>
                                                                            </td>
                                                                            <td style={{ padding: '14px 8px' }}><span className="badge badge-warning" style={{ fontSize: 9 }}>{t('common.counter').toUpperCase()}</span></td>
                                                                            <td style={{ padding: '14px 8px' }}>
                                                                                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--warning)', background: 'rgba(245, 158, 11, 0.08)', padding: '2px 8px', borderRadius: 4 }}>
                                                                                    {counter.unit}
                                                                                </span>
                                                                            </td>
                                                                            <td style={{ padding: '14px 8px' }}>
                                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'monospace' }}>
                                                                                    {counter.counterUUID}
                                                                                </div>
                                                                            </td>
                                                                            <td style={{ padding: '14px 8px', textAlign: 'right' }}>
                                                                                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                                                                                    <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/admin/counters/${counter.counterUUID}`)} style={{ padding: '4px 12px', fontSize: 11, borderRadius: 6 }}>{t('companyDetail.viewData')}</button>
                                                                                    <button className="btn btn-primary btn-sm btn-icon" onClick={() => openRenameModal('counter', counter._id, counter.counterName, counter.unit)} style={{ width: 28, height: 28, borderRadius: 6, background: 'rgba(79, 142, 247, 0.08)', border: 'none', color: 'var(--primary)' }} title={t('companyDetail.renameCounter')}><MdEdit /></button>
                                                                                    <button className="btn btn-danger btn-sm" onClick={() => handleDeleteCounter(counter._id, counter.counterName)} disabled={deletingId === counter._id} style={{ padding: '4px 12px', fontSize: 11, borderRadius: 6 }}>{t('common.delete')}</button>
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    )}
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </motion.div>
                            ))
                        )}
                    </div>
                </div>

                {/* Modals */}
                {showAddSystem && (
                    <AddSystemModal
                        companyId={id}
                        onClose={() => setShowAddSystem(false)}
                        onSuccess={(newSystem) => {
                            setSystems(prev => [...prev, newSystem]);
                            setExpandedSystems(prev => ({ ...prev, [newSystem._id]: true }));
                            setShowAddSystem(false);
                        }}
                    />
                )}

                {showAddSensor && (
                    <AddSensorModal
                        companyId={id}
                        onClose={() => setShowAddSensor(false)}
                        onSuccess={() => { setShowAddSensor(false); fetchData(); }}
                    />
                )}

                {showAddCounter && (
                    <AddCounterModal
                        companyId={id}
                        onClose={() => setShowAddCounter(false)}
                        onSuccess={() => { setShowAddCounter(false); fetchData(); }}
                    />
                )}

                {showAddStatusSignal && (
                    <AddStatusSignalModal
                        companyId={id}
                        onClose={() => setShowAddStatusSignal(false)}
                        onSuccess={() => { setShowAddStatusSignal(false); fetchData(); }}
                    />
                )}

                {showAddPlc && (
                    <AddPlcModal
                        companyId={id}
                        onClose={() => setShowAddPlc(false)}
                        onSuccess={() => { setShowAddPlc(false); fetchData(); }}
                    />
                )}

                {renameConfig.show && (
                    <RenameModal
                        targetId={renameConfig.targetId}
                        currentName={renameConfig.currentName}
                        currentUnit={renameConfig.currentUnit}
                        targetType={renameConfig.targetType}
                        onClose={() => setRenameConfig({ show: false, targetId: null, targetType: '', currentName: '', currentUnit: '' })}
                        onSuccess={() => {
                            setRenameConfig({ show: false, targetId: null, targetType: '', currentName: '', currentUnit: '' });
                            fetchData();
                        }}
                    />
                )}

                {thresholdSensor && (
                    <SensorThresholdModal
                        sensor={thresholdSensor}
                        onClose={() => setThresholdSensor(null)}
                        onSuccess={() => { setThresholdSensor(null); fetchData(); }}
                    />
                )}
            </div>
        </div>
    );
}
